import { num, plural } from "./format";

/** Lo que trae el dry-run de `send()` (`activa.service.ts`) sobre los que no se encolan, cada uno con su contador. */
export interface AlcanceDryRun {
  encolados?: number;
  /** dijeron que no, o la política estricta y el archivo no dice que sí */
  excluidosSinOptIn?: number;
  bloqueadosDnc?: number;
  porFatiga?: number;
  /** la regla de fatiga de la marca que miró el envío: los días (los de la marca o los de la campaña, el mayor) y K */
  fatigaDias?: number;
  fatigaMaximo?: number;
  duplicadosCelular?: number;
  fueraDeCanal?: number;
  yaEntregados?: number;
  sinMedioDeContacto?: number;
  sinReintentos?: number;
  /** mails frenados porque no se puede armar su link de baja (falta FRONTEND_URL), y el porqué */
  emailsSinLinkBaja?: number;
  motivoEmail?: string;
  /** reparto QR / API: los que van por WhatsApp oficial, y los de WhatsApp que no entraron en el reparto */
  porApi?: number;
  fueraDelReparto?: number;
}

/**
 * Por qué quedan afuera los que no reciben, cada motivo con su nombre y su número.
 *
 * QA del 08/10: el pie de la previa decía «el resto queda afuera por consentimiento, bajas o repetidos» siempre, y
 * el resto podía ser otra cosa: los mails frenados porque falta FRONTEND_URL (que no es ninguna de las tres), la
 * fatiga, el canal elegido, quien ya lo recibió o quien no tiene mail ni celular. Ahora se dice lo que es, con los
 * contadores del mismo dry-run que calcula el alcance. Los mails frenados van aparte (`avisoMails`): no es una
 * exclusión de la persona, es algo que falta configurar y que se destraba solo cuando se configura.
 */
export function quienesQuedanAfuera(r: AlcanceDryRun | null | undefined): { motivos: string[]; avisoMails: string | null } {
  const v = (n: unknown) => Math.max(0, Number(n) || 0);
  const motivos: string[] = [];
  const agregar = (n: number, texto: string) => { if (n > 0) motivos.push(`${num(n)} ${texto}`); };
  const sinSi = v(r?.excluidosSinOptIn);
  agregar(sinSi, "sin consentimiento");
  const bajas = v(r?.bloqueadosDnc);
  agregar(bajas, plural(bajas, "que pidió la baja", "que pidieron la baja"));
  // la regla de la marca (29/10/2026): cuenta lo que salió por WhatsApp oficial y por QR
  const fatiga = v(r?.porFatiga);
  const dias = v(r?.fatigaDias);
  const maximo = Math.max(1, v(r?.fatigaMaximo) || 1);
  const ventana = dias === 1 ? "en el último día" : `en los últimos ${num(dias)} días`;
  agregar(
    fatiga,
    !dias
      ? plural(fatiga, "que recibió otro mensaje hace poco", "que recibieron otro mensaje hace poco")
      : maximo === 1
        ? `${plural(fatiga, "que ya recibió", "que ya recibieron")} un mensaje ${ventana} (WhatsApp oficial o por QR)`
        : `${plural(fatiga, "que ya recibió", "que ya recibieron")} ${num(maximo)} mensajes ${ventana} (sumando WhatsApp oficial y por QR)`,
  );
  const canal = v(r?.fueraDeCanal);
  agregar(canal, plural(canal, "que sólo puede recibir por el otro canal", "que sólo pueden recibir por el otro canal"));
  const repetidos = v(r?.duplicadosCelular);
  agregar(repetidos, "con el celular repetido");
  const recibieron = v(r?.yaEntregados);
  agregar(recibieron, plural(recibieron, "que ya lo recibió", "que ya lo recibieron"));
  const sinContacto = v(r?.sinMedioDeContacto);
  agregar(sinContacto, "sin mail ni celular");
  const agotados = v(r?.sinReintentos);
  agregar(agotados, "con 3 intentos fallidos");
  // el reparto QR / API: no es una exclusión de la persona, es la otra vía (o no le tocó ninguna)
  const api = v(r?.porApi);
  agregar(api, plural(api, "que va por WhatsApp oficial (reparto)", "que van por WhatsApp oficial (reparto)"));
  const fueraReparto = v(r?.fueraDelReparto);
  agregar(fueraReparto, plural(fueraReparto, "que no entró en el reparto entre QR y API", "que no entraron en el reparto entre QR y API"));
  const mails = v(r?.emailsSinLinkBaja);
  agregar(mails, plural(mails, "mail que todavía no puede salir", "mails que todavía no pueden salir"));
  const motivoMail = String(r?.motivoEmail || "").trim();
  return { motivos, avisoMails: mails > 0 && motivoMail ? motivoMail : null };
}

/** «a, b y c» */
export function enumerar(partes: string[]): string {
  if (partes.length <= 1) return partes.join("");
  return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
}
